import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  RotateCcw,
  CheckCircle2,
  Sparkles,
  Instagram,
  Music2,
} from "lucide-react";
import { ShoppingBag } from "lucide-react";
import { CREATORS } from "../data/creators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_VIDEO =
  "https://videos.pexels.com/video-files/2098988/2098988-hd_1920_1080_30fps.mp4";
const SERVICES_POSTER =
  "https://images.unsplash.com/photo-1501420264597-23296a7e6a46?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const LIVE_FEED = [
  { user: "maya_rivers", accent: "#ff3d7f", cat: "Audience Growth", msg: "grew my reach 300% this quarter 🚀" },
  { user: "andre.cole", accent: "#3d9dff", cat: "Community & Engagement", msg: "my community actually shows up now 💬" },
  { user: "keia.b", accent: "#b06bff", cat: "Personal Brand Development", msg: "finally found my voice ✨" },
  { user: "the.plug", accent: "#00e0a4", cat: "Influencer Networking & Events", msg: "met my next big collab 🤝" },
  { user: "vip.guest", accent: "#ffb03d", cat: "Event Experiences", msg: "that mixer was absolutely iconic 🎟️" },
  { user: "jordan_x", accent: "#ff3d7f", cat: "Audience Growth", msg: "hit 1M faster than I dreamed 📈" },
  { user: "sana.v", accent: "#3d9dff", cat: "Community & Engagement", msg: "retention is up, haters down 🖤" },
  { user: "devon", accent: "#00e0a4", cat: "Influencer Networking & Events", msg: "red carpet, here I come 💫" },
];

/* ---------------- Slides ---------------- */

function HomeSlide() {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#0a0a0a] px-6 text-center">
      {/* animated glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full opacity-40 blur-[100px]"
          style={{ background: "radial-gradient(circle, #ff3d7f, transparent 60%)" }} />
        <div className="absolute left-1/3 top-1/3 h-[380px] w-[380px] rounded-full opacity-30 blur-[100px]"
          style={{ background: "radial-gradient(circle, #3d9dff, transparent 60%)" }} />
      </div>
      {[...Array(18)].map((_, i) => (
        <motion.span
          key={i}
          className="absolute h-1 w-1 rounded-full bg-white/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0], y: [0, -30, 0] }}
          transition={{ duration: 3 + (i % 5), repeat: Infinity, delay: i * 0.3 }}
          style={{ left: `${(i * 53) % 100}%`, top: `${(i * 37) % 100}%` }}
        />
      ))}

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 mb-6 text-[10px] uppercase tracking-[0.5em] text-white/50"
      >
        Amani Talent Agency
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, scale: 0.9, filter: "blur(14px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif-lux relative z-10 text-6xl font-light leading-[0.95] tracking-tight text-white sm:text-7xl md:text-8xl"
      >
        Your Potential.
        <br />
        <span className="italic text-white/75">Our Purpose.</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 1 }}
        className="relative z-10 mt-8 max-w-sm text-sm font-light leading-relaxed tracking-wide text-white/60"
      >
        The creator growth agency for the next generation of influence.
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-24 z-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-white/40"
      >
        Tap to begin <ChevronRight size={14} />
      </motion.div>
    </div>
  );
}

function ServicesSlide({ paused }) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <video
        className="absolute inset-0 h-full w-full object-cover"
        style={{ filter: "blur(14px) brightness(0.35)", transform: "scale(1.15)" }}
        autoPlay
        muted
        loop
        playsInline
        poster={SERVICES_POSTER}
      >
        <source src={SERVICES_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative z-10 flex h-full flex-col px-6 pt-20 md:px-12">
        <p className="text-[10px] uppercase tracking-[0.4em] text-white/50">What we do</p>
        <h2 className="font-serif-lux mt-3 text-5xl font-light leading-none tracking-tight text-white md:text-6xl">
          Our Services
        </h2>
        <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-white/60">
          Five ways we turn creators into cultural forces — live from the community.
        </p>
      </div>

      {/* TikTok-live style comment feed rising from bottom */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[62%] overflow-hidden px-5 md:px-12"
        style={{ maskImage: "linear-gradient(to top, black 55%, transparent)", WebkitMaskImage: "linear-gradient(to top, black 55%, transparent)" }}
        data-testid="services-feed"
      >
        <div className="animate-feed absolute bottom-0 flex w-full flex-col gap-3" style={{ animationPlayState: paused ? "paused" : "running" }}>
          {[...LIVE_FEED, ...LIVE_FEED].map((c, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-medium text-black"
                style={{ background: c.accent }}>
                {c.user[0].toUpperCase()}
              </span>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/10 px-4 py-2.5 backdrop-blur-md">
                <p className="text-[11px] font-medium tracking-wide" style={{ color: c.accent }}>
                  @{c.user} · <span className="text-white">{c.cat}</span>
                </p>
                <p className="mt-0.5 text-sm font-light leading-snug text-white/85">{c.msg}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GlitterCursor() {
  useEffect(() => {
    const colors = ["#ff2e9a", "#ff8fd0", "#ffffff", "#ffd1ec", "#c77dff"];
    let last = 0;
    const spawn = (x, y) => {
      const now = Date.now();
      if (now - last < 35) return;
      last = now;
      const s = document.createElement("span");
      s.textContent = "\u2726";
      const size = 8 + Math.random() * 12;
      s.style.cssText =
        `position:fixed;left:${x}px;top:${y}px;pointer-events:none;z-index:9998;` +
        `color:${colors[Math.floor(Math.random() * colors.length)]};font-size:${size}px;` +
        `transform:translate(-50%,-50%);transition:transform .9s ease-out,opacity .9s ease-out;text-shadow:0 0 6px currentColor;`;
      document.body.appendChild(s);
      requestAnimationFrame(() => {
        s.style.transform = `translate(-50%, ${28 + Math.random() * 24}px) rotate(${Math.random() * 180 - 90}deg) scale(0.2)`;
        s.style.opacity = "0";
      });
      setTimeout(() => s.remove(), 900);
    };
    const onPointer = (e) => spawn(e.clientX, e.clientY);
    const onTouch = (e) => { const t = e.touches[0]; if (t) spawn(t.clientX, t.clientY); };
    window.addEventListener("pointermove", onPointer);
    window.addEventListener("touchmove", onTouch);
    return () => {
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("touchmove", onTouch);
    };
  }, []);
  return null;
}

function PinnedSocials({ creator }) {
  const glam = creator.theme === "glam";
  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-30 border-t backdrop-blur-lg ${glam ? "border-pink-300/40 bg-[#2a0518]/75" : "border-white/10 bg-black/60"}`}
      data-testid={`creator-${creator.slug}-social-bar`}
    >
      <div className="flex items-center justify-center gap-8 px-4 py-3 md:gap-12">
        {creator.socials.map((s) => {
          const Icon = s.type === "tiktok" ? Music2 : Instagram;
          return (
            <a
              key={s.type}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`creator-${creator.slug}-social-${s.type}`}
              className="group flex items-center gap-2 text-white/85 transition-colors hover:text-white"
            >
              <Icon size={18} strokeWidth={1.6} style={{ color: creator.accent }} />
              <span className="flex flex-col leading-tight">
                <span className="text-xs font-medium">{s.handle}</span>
                {s.followers && <span className="text-[10px] text-white/50">{s.followers} followers</span>}
              </span>
            </a>
          );
        })}
      </div>
      {creator.brand && <BrandShop brand={creator.brand} accent={creator.accent} />}
    </div>
  );
}

function BrandShop({ brand, accent }) {
  const [revealed, setRevealed] = useState(false);

  const handleClick = () => {
    setRevealed(true);
    if (navigator.clipboard) navigator.clipboard.writeText(brand.code).catch(() => {});
    toast.success(`Code ${brand.code} copied — ${brand.discount}!`);
    window.open(brand.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="px-4 pb-4">
      <button
        onClick={handleClick}
        data-testid="shop-princess-polly"
        className="shop-glow flex w-full items-center justify-center gap-2 rounded-full py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-black"
        style={{ background: `linear-gradient(90deg, ${accent}, #ff9ed4)` }}
      >
        <ShoppingBag size={15} strokeWidth={2.4} />
        Shop {brand.name} · {brand.discount}
      </button>
      {revealed && (
        <p className="mt-2 text-center text-[11px] tracking-wide text-white/80" data-testid="promo-code">
          Promo code: <span className="font-bold text-white">{brand.code}</span> (copied!)
        </p>
      )}
    </div>
  );
}

function MySpaceProfile({ creator }) {
  const glam = creator.theme === "glam";
  const barBg = glam ? "linear-gradient(90deg,#ff2e9a,#ff8fd0)" : "linear-gradient(90deg,#ff3b30,#7a0f0a)";
  const boxCls = glam ? "border-pink-200/40 bg-white/10" : "border-white/15 bg-white/[0.04]";
  const ring = creator.accent;

  const SectionHead = ({ children }) => (
    <div className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-black" style={{ background: barBg }}>
      {children}
    </div>
  );

  return (
    <div className="relative h-full w-full overflow-hidden">
      {glam ? (
        <>
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 15%, #ff7ac2, #b7176e 45%, #2a0518 100%)" }} />
          <div
            className="absolute inset-0 opacity-25 mix-blend-screen"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1616000315178-317bbc76ea71?crop=entropy&cs=srgb&fm=jpg&q=70&w=900')", backgroundSize: "cover" }}
          />
          <GlitterCursor />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-[#0a0a0a]" />
          <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 70% 15%, #ff3b30, transparent 55%)" }} />
        </>
      )}

      {creator.brand && (
        <div
          data-testid="brand-partner-badge"
          className="absolute right-3 top-10 z-40 flex items-center gap-1.5 rounded-full border border-white/40 bg-black/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur-md"
        >
          <ShoppingBag size={12} strokeWidth={2} style={{ color: creator.accent }} />
          Brand Partner: {creator.brand.name}
        </div>
      )}

      <div className="relative z-10 mx-auto flex h-full max-w-md flex-col px-4 pt-12 pb-24">
        {/* window title bar */}
        <div className="flex items-center justify-between rounded-t-lg px-4 py-2 text-black" style={{ background: barBg }}>
          <span className="text-sm font-extrabold tracking-wide">
            {glam ? "\u273f " : ""}{creator.name}'s Profile{glam ? " \u273f" : ""}
          </span>
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase">
            <span className="h-2 w-2 rounded-full bg-green-400 shadow-[0_0_6px_#4ade80]" /> {creator.mood.replace(/[^a-zA-Z ]/g, "").trim() || "online"}
          </span>
        </div>

        {/* identity row */}
        <div className={`grid grid-cols-[auto_1fr] gap-4 rounded-b-lg border p-4 ${boxCls}`}>
          <div className="h-32 w-28 overflow-hidden rounded-md border-2 shadow-lg" style={{ borderColor: ring }}>
            <img src={creator.photo} alt={creator.name} data-testid={`creator-${creator.slug}-photo`} className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0">
            <h2 className="text-4xl font-light leading-none text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {creator.name}
            </h2>
            <p className="mt-1 text-xs text-white/70">{creator.handle}</p>
            <p className="mt-2 text-sm italic leading-snug text-white/85">{creator.tagline}</p>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-2xl font-black" style={{ color: ring }}>{creator.followers}</span>
              <span className="text-[10px] uppercase tracking-widest text-white/50">total followers</span>
            </div>
          </div>
        </div>

        {/* stats table */}
        <div className={`mt-3 overflow-hidden rounded-lg border ${boxCls}`}>
          <SectionHead>{glam ? "\u2605 Stats \u2605" : "Stats"}</SectionHead>
          <table className="w-full text-sm" data-testid={`creator-${creator.slug}-stats`}>
            <tbody className="[&_td]:border-b [&_td]:border-white/10 [&_td]:px-4 [&_td]:py-2">
              {creator.socials.map((s) => (
                <tr key={s.type}>
                  <td className="text-white/50">{s.type === "tiktok" ? "TikTok" : "Instagram"}</td>
                  <td className="truncate text-white/80">{s.handle}</td>
                  <td className="whitespace-nowrap text-right font-bold" style={{ color: ring }}>{s.followers || "—"}</td>
                </tr>
              ))}
              <tr>
                <td className="text-white/50">Total Followers</td>
                <td className="text-white/80">across platforms</td>
                <td className="text-right font-bold" style={{ color: ring }}>{creator.followers}</td>
              </tr>
              {creator.profile?.views && (
                <tr>
                  <td className="text-white/50">Profile Views</td>
                  <td className="text-white/80">since launch</td>
                  <td className="text-right font-bold text-white">{creator.profile.views}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* about me */}
        <div className={`mt-3 overflow-hidden rounded-lg border ${boxCls}`}>
          <SectionHead>{glam ? "\u2661 About Me \u2661" : "About Me"}</SectionHead>
          <p className="px-4 py-3 text-sm font-light leading-relaxed text-white/85">{creator.about}</p>
        </div>
      </div>

      <PinnedSocials creator={creator} />
    </div>
  );
}

function CreatorSlide({ creator }) {
  return <MySpaceProfile creator={creator} />;
}



function ApplicationSlide({ onReplay }) {
  const [form, setForm] = useState({ name: "", email: "", socials: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.socials) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application received! We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white placeholder-white/30 outline-none transition-colors focus:border-white";

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#0a0a0a] px-6">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute right-1/4 top-1/4 h-[360px] w-[360px] rounded-full opacity-30 blur-[100px]"
          style={{ background: "radial-gradient(circle, #ff3d7f, transparent 60%)" }} />
        <div className="absolute bottom-1/4 left-1/4 h-[360px] w-[360px] rounded-full opacity-25 blur-[100px]"
          style={{ background: "radial-gradient(circle, #3d9dff, transparent 60%)" }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {done ? (
          <div className="flex flex-col items-center text-center" data-testid="application-success">
            <CheckCircle2 size={44} strokeWidth={1} className="text-white" />
            <h2 className="font-serif-lux mt-6 text-4xl font-light tracking-tight text-white">You're in the queue.</h2>
            <p className="mt-4 text-sm font-light leading-relaxed text-white/60">
              Thanks, {form.name.split(" ")[0]}. Our team will review your profile and reach out soon.
            </p>
            <button
              onClick={onReplay}
              data-testid="replay-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-white px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80"
            >
              <RotateCcw size={15} /> Replay
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center gap-2 text-white/50">
              <Sparkles size={16} strokeWidth={1.4} />
              <span className="text-[10px] uppercase tracking-[0.35em]">Creator Application</span>
            </div>
            <h2 className="font-serif-lux text-5xl font-light leading-none tracking-tight text-white">
              Join the<br />Agency.
            </h2>
            <p className="mt-4 text-sm font-light leading-relaxed text-white/50">
              Think you're next? Put yourself on our radar.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="application-form">
              <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Full Name" data-testid="app-name" className={inputCls} />
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email Address" data-testid="app-email" className={inputCls} />
              <input type="text" name="socials" value={form.socials} onChange={handleChange} placeholder="Social handles (@you on IG, TikTok)" data-testid="app-socials" className={inputCls} />
              <div className="flex items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="app-submit-button"
                  className="group inline-flex flex-1 items-center justify-center gap-3 rounded-none bg-white px-8 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80 disabled:opacity-50"
                >
                  {submitting ? "Sending" : "Submit"}
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
                <button
                  type="button"
                  onClick={onReplay}
                  data-testid="replay-button-form"
                  className="inline-flex items-center gap-2 rounded-none border border-white/25 px-6 py-4 text-[11px] uppercase tracking-[0.3em] text-white/70 transition-colors hover:border-white hover:text-white"
                >
                  <RotateCcw size={14} /> Replay
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Shell ---------------- */

export default function StoriesExperience() {
  const startAt = (() => {
    const n = parseInt(new URLSearchParams(window.location.search).get("s") || "0", 10);
    return Number.isNaN(n) ? 0 : n;
  })();
  const [current, setCurrent] = useState(startAt);
  const [paused, setPaused] = useState(false);
  const holdTimer = useRef(null);
  const heldRef = useRef(false);

  const slides = [
    { key: "home", render: () => <HomeSlide />, interactive: false },
    { key: "services", render: (p) => <ServicesSlide paused={p} />, interactive: false },
    ...CREATORS.map((c) => ({ key: `creator-${c.slug}`, render: (p) => <CreatorSlide creator={c} paused={p} />, interactive: false })),
    { key: "apply", render: () => <ApplicationSlide onReplay={() => setCurrent(0)} />, interactive: true },
  ];

  const total = slides.length;
  const next = useCallback(() => setCurrent((c) => Math.min(total - 1, c + 1)), [total]);
  const prev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);

  // press-and-hold to pause; quick tap navigates
  const startHold = () => {
    heldRef.current = false;
    holdTimer.current = setTimeout(() => {
      heldRef.current = true;
      setPaused(true);
    }, 220);
  };
  const endHold = (action) => {
    clearTimeout(holdTimer.current);
    if (heldRef.current) {
      setPaused(false);
      heldRef.current = false;
    } else if (action) {
      action();
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const active = slides[current];

  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-black" data-testid="stories-experience">
      {/* progress bars */}
      <div className="absolute inset-x-0 top-0 z-40 flex gap-1.5 px-3 pt-3" data-testid="stories-progress">
        {slides.map((s, idx) => (
          <div key={s.key} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full bg-white transition-all duration-500"
              style={{ width: idx <= current ? "100%" : "0%" }}
            />
          </div>
        ))}
      </div>

      {/* brand tag */}
      <div className="absolute left-4 top-6 z-40 font-serif-lux text-sm tracking-[0.25em] text-white/80">
        AMANI
      </div>

      {/* slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.key}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          {active.render(paused)}
        </motion.div>
      </AnimatePresence>

      {/* pause indicator */}
      {paused && !active.interactive && (
        <div className="absolute left-1/2 top-16 z-40 -translate-x-1/2 rounded-full bg-black/60 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-white/80 backdrop-blur-md" data-testid="paused-indicator">
          Paused
        </div>
      )}

      {/* tap zones — only on non-interactive slides. Quick tap navigates, press-and-hold pauses */}
      {!active.interactive && (
        <div className="absolute inset-0 z-20 flex">
          <button
            aria-label="previous"
            data-testid="tap-prev"
            onPointerDown={startHold}
            onPointerUp={() => endHold(prev)}
            onPointerLeave={() => endHold(null)}
            className="h-full w-1/3"
          />
          <button
            aria-label="next"
            data-testid="tap-next"
            onPointerDown={startHold}
            onPointerUp={() => endHold(next)}
            onPointerLeave={() => endHold(null)}
            className="h-full w-2/3"
          />
        </div>
      )}

      {/* desktop chevrons (above tap zones + socials) */}
      {current > 0 && (
        <button
          onClick={prev}
          data-testid="nav-prev"
          className="absolute left-3 top-1/2 z-40 hidden -translate-y-1/2 rounded-full bg-black/40 p-2 text-white/80 backdrop-blur-md transition-colors hover:bg-black/70 md:block"
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {current < total - 1 && (
        <button
          onClick={next}
          data-testid="nav-next"
          className="absolute right-3 top-1/2 z-40 hidden -translate-y-1/2 rounded-full bg-black/40 p-2 text-white/80 backdrop-blur-md transition-colors hover:bg-black/70 md:block"
        >
          <ChevronRight size={22} />
        </button>
      )}
    </div>
  );
}
